import React, { useContext, useEffect, useState } from 'react';
import styles from './index.module.scss';
import HeadContainer from '../head-container';
import PhoneInputComponent from './components/phone-input-component';
import OtpComponent from './components/otp-component';
import ResultComponent from './components/result-component';
import { getTrackBackground, Range } from 'react-range';
import { getImageFromS3 } from '../../imageService';
import classNames from 'classnames';
import { DataLayerEventContext } from '../../contexts/dataLayerEventContext';
import { loadRecaptchaV3 } from '../../utils/recaptchaV3';

// reCaptcha v3 (invisible) for the feedback form. Empty until the real v3 site
// key is set in the env files — while empty the form behaves exactly as before
// (graceful degradation). The token itself is generated at submit time, right
// before POST /feedback (see phone-input-component). Different key from the v2
// checkbox key used by the registration forms.
const RECAPTCHA_V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

const factoriesList = [
  'ПрАТ "Філіп Морріс Україна"',
  'АТ "Імперіал Тобакко Продакшн" на замов...',
  '"Філіп Морріс С.А."',
  'Інше'
];

const RangeItem = ({ rangeValue, setRangeValue, labelText }) => {
  return (
    <>
      <label className={styles.rangeTitle}>{labelText}</label>
      <Range
        step={1}
        min={1}
        max={5}
        values={rangeValue}
        ariaLabel="Дизайн пачки"
        renderTrack={({ props, children }) => (
          <div
            {...props}
            style={{
              ...props.style,
              display: 'flex',
              position: 'relative',
              height: '22px',
              width: '100%',
              background: getTrackBackground({
                values: rangeValue,
                colors: ['#2E0073', '#955DD6'],
                min: 1,
                max: 5
              }),
              borderRadius: '5px'
            }}
          >
            {children}
          </div>
        )}
        renderThumb={({ props }) => <div {...props} className={styles.thumb} />}
        onChange={(values) => setRangeValue(values)}
      />
      <div className={styles.rangeItemList}>
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
    </>
  );
};

const ReviewFlowScreen = ({
  setScreenNumber,
  setSubmitReviewScreenNumber,
  innerScreenNumber = null,
  setInnerScreenNumber = null
}) => {
  const { sendEvent } = useContext(DataLayerEventContext);

  // History-driven when the parent wires up `setInnerScreenNumber`,
  // otherwise falls back to plain internal state.
  const isHistoryEnabled = !!setInnerScreenNumber;

  const sanitizeText = (v) => (v || '').replace(/[^A-Za-zА-Яа-яІЇії’ \-]/g, '');
  const [designRangeValue, setDesignRangeValue] = useState([5]);
  const [tasteQualitiesRangeValue, setTasteQualitiesRangeValue] = useState([5]);
  const [comment, setComment] = useState('');
  const [isCommentError, setIsCommentError] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const [internalPhoneNumberScreen, setInternalPhoneNumberScreen] =
    useState(false);
  const [internalOtpScreen, setInternalOtpScreen] = useState(false);
  const [internalIsOpenResult, setInternalIsOpenResult] = useState(false);

  const phoneNumberScreen = isHistoryEnabled
    ? innerScreenNumber === 101
    : internalPhoneNumberScreen;
  const otpScreen = isHistoryEnabled
    ? innerScreenNumber === 102
    : internalOtpScreen;
  const isOpenResult = isHistoryEnabled
    ? innerScreenNumber === 103
    : internalIsOpenResult;

  const handleSetPhoneNumberScreen = (val) => {
    if (isHistoryEnabled) {
      if (val) setInnerScreenNumber(101);
      else if (innerScreenNumber === 101) window.history.back();
    } else {
      setInternalPhoneNumberScreen(val);
    }
  };

  // Переходи, що міняють одразу кілька внутрішніх кроків, треба робити одним
  // викликом: `innerScreenNumber` — це проп, і в межах одного обробника він
  // ще має старе значення, тому пара "показати новий крок + сховати старий"
  // спричиняла зайвий window.history.back() і повертала користувача назад.
  const goToInnerScreen = (target) => {
    if (isHistoryEnabled) {
      setInnerScreenNumber(target);
    } else {
      setInternalPhoneNumberScreen(target === 101);
      setInternalOtpScreen(target === 102);
      setInternalIsOpenResult(target === 103);
    }
  };

  const [images, setImages] = useState([]);
  const [isImagesError, setIsImagesError] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [otpStatus, setOtpStatus] = useState(null);
  const [isCheckedMailing, setIsCheckedMailing] = useState(false);
  const [isCheckedMailingError, setIsCheckedMailingError] = useState(false);

  useEffect(() => {
    sendEvent('review');
  }, []);

  useEffect(() => {
    // Preload the reCaptcha v3 script so its badge is present on the feedback
    // form (before the button) and execute() is instant at submit. No-op when
    // the site key is not configured.
    loadRecaptchaV3(RECAPTCHA_V3_SITE_KEY);
  }, []);

  const onSubmitReview = () => {
    const isValidForm = images?.length > 0 && comment?.length !== 0;

    setIsCommentError(comment?.trim()?.length === 0);
    setIsImagesError(images?.length === 0);

    handleSetPhoneNumberScreen(isValidForm);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);

    setImages(newImages);
    setPreviews(newPreviews);
  };

  const handleUploadFile = async (event) => {
    if (previews?.length >= 10 && images?.length >= 10) return;

    const filesList = Array.from(event.target.files);
    const allowed = filesList
      .filter((file) => {
        const type = (file.type || '').toLowerCase();
        const name = (file.name || '').toLowerCase();
        const extOk = /\.(jpg|jpeg|png)$/.test(name);
        const typeOk = type === 'image/jpeg' || type === 'image/png';
        return extOk || typeOk;
      })
      .slice(0, 10 - images.length);

    if (allowed.length === 0) return; // silently ignore not allowed

    setImages((prevImages) => [...prevImages, ...allowed].slice(0, 10));

    const newPreviews = allowed.map((file) => URL.createObjectURL(file));
    setPreviews((prevPreviews) =>
      [...prevPreviews, ...newPreviews].slice(0, 10)
    );
  };

  return (
    <div className={styles.container}>
      {!phoneNumberScreen && !otpScreen && !isOpenResult && (
        <div
          className={styles.reviewContainer}
          style={{
            backgroundImage: `url("${getImageFromS3(
              'images/review-screen/violet-background.png'
            )}")`
          }}
        >
          <HeadContainer setScreenNumber={setScreenNumber} />
          <p className={styles.title}>
            Нам дуже важливий твій відгук про продукт, щоб стати краще
          </p>
          <p className={styles.subtitle}>
            Залишай відгук і отримуй поповнення мобільного рахунку на 30 грн за
            приділений час
          </p>
          <p className={styles.subtitleDescription}>
            опція поповнення доступна раз на місяць
          </p>
          <RangeItem
            labelText="Дизайн пачки"
            rangeValue={designRangeValue}
            setRangeValue={setDesignRangeValue}
          />
          <RangeItem
            labelText="Смакові властивості"
            rangeValue={tasteQualitiesRangeValue}
            setRangeValue={setTasteQualitiesRangeValue}
          />
          <div className={styles.commentContainer}>
            <label>Твій коментар:</label>
            <textarea
              className={classNames(isCommentError && styles.textAreaError)}
              value={comment}
              maxLength={1000}
              onChange={(event) => {
                const val = sanitizeText(event.target.value).slice(0, 1000);
                setComment(val);
              }}
              onFocus={() => setIsCommentError(false)}
              placeholder="Залиште свій коментар"
            />
          </div>
          {isCommentError && (
            <p className={styles.textError}>Обов'язкове поле</p>
          )}
          <div className={styles.imageExportContainer}>
            <label>Додай фото продукту (можна декілька):</label>
            <input
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              style={{ display: 'none' }}
              id="upload-image"
              multiple
              type="file"
              className="w-full h-full"
              onChange={handleUploadFile}
              onClick={() => setIsImagesError(false)}
            />
            <label htmlFor="upload-image">
              <img
                src={getImageFromS3(
                  isImagesError
                    ? 'images/error-image-export.png'
                    : 'images/image-export.png'
                )}
                alt="image-export"
              />
            </label>
          </div>
          {isImagesError && (
            <p className={styles.textError}>Обов'язкове поле</p>
          )}
          {previews?.length > 0 && (
            <div className={styles.previewImageList}>
              {previews.map((preview, index) => (
                <div className={styles.imageItem} key={index}>
                  <img src={preview} alt={`Preview ${index}`} />
                  <img
                    src={getImageFromS3('images/remove-image-icon.png')}
                    alt="remove-icon"
                    onClick={() => handleRemoveImage(index)}
                  />
                </div>
              ))}
            </div>
          )}
          <div className={styles.infoContainer}>
            <label>
              Додай фото пачки, що допоможуть ідентифікувати проблему та партію
              продукту. Нижче приклад, головне — це чітке зображення інформації
              на дні пачки.
            </label>
            <img
              src={getImageFromS3('images/review-info.png')}
              alt="reviewImage"
            />
          </div>
          <div className={styles.mailing}>
            <input
              type="checkbox"
              id="mailing"
              name="mailing"
              onClick={() => {
                setIsCheckedMailing(!isCheckedMailing);
                setIsCheckedMailingError(false);
              }}
              checked={isCheckedMailing}
            />
            <span
              className={classNames(
                styles.checkmark,
                isCheckedMailingError && styles.errorInput
              )}
            ></span>
            <label htmlFor="mailing">Хочу отримати зворотній зв'язок 😊</label>
          </div>
          <button
            className={styles.menuButton}
            style={{ marginTop: 11 }}
            onClick={onSubmitReview}
            disabled={isSubmitDisabled}
          >
            Залишити відгук
          </button>
          <button
            className={styles.reviewButton}
            style={{ marginTop: 14 }}
            onClick={() => setScreenNumber(1)}
          >
            Повернутися до меню
          </button>
        </div>
      )}
      {phoneNumberScreen && !otpScreen && !isOpenResult && (
        <PhoneInputComponent
          handleBackButtonClick={() => handleSetPhoneNumberScreen(false)}
          setScreenNumber={setScreenNumber}
          data={{
            comment,
            design: designRangeValue[0],
            flavor: tasteQualitiesRangeValue[0],
            files: images,
            callback: isCheckedMailing
          }}
          handleSuccessOtpSend={(status) => {
            if (status === 'PENDING_OTP') {
              goToInnerScreen(102);
            } else {
              goToInnerScreen(103);
            }
            setOtpStatus(status);
          }}
        />
      )}
      {!phoneNumberScreen &&
        !isOpenResult &&
        otpScreen &&
        otpStatus === 'PENDING_OTP' && (
          <OtpComponent
            setScreenNumber={setScreenNumber}
            handleChangePhoneNumber={() => {
              goToInnerScreen(101);
            }}
            successSendOtp={() => {
              //sendEvent('passed_authorization');
              goToInnerScreen(103);
            }}
          />
        )}
      {isOpenResult && !phoneNumberScreen && !otpScreen && (
        <ResultComponent
          setScreenNumber={setScreenNumber}
          otpStatus={otpStatus}
        />
      )}
    </div>
  );
};

export default ReviewFlowScreen;
